import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SiteSettingsProvider } from "@/contexts/SiteSettingsContext";
import { InlineEditProvider } from "@/contexts/InlineEditContext";
import InlineEditToggle from "@/components/InlineEditToggle";
import { useEffect, lazy, Suspense } from "react";
import React from "react";
import HomePage from "@/pages/HomePage";
import PageResolver from "@/pages/PageResolver";
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieBanner from "@/components/CookieBanner";
import UniversalTextEditor from "@/components/UniversalTextEditor";

// NOTE: A global <SEOSync /> wrapper that called useSEOMeta() on every route
// was previously mounted here. It was removed because react-helmet-async
// (used on every page) and the build-time prerender (137 static HTML files)
// already own the meta tags. The hook was creating DUPLICATE
// `<meta name="description">` tags instead of cooperating, causing crawlers
// to read the generic fallback instead of the per-page Helmet value.
// The hook file `/src/hooks/useSEOMeta.js` is kept for future manual use on
// any route that lacks proper Helmet handling.

// Lazy-loaded routes (code-split)
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const IndustriesPage = lazy(() => import("@/pages/IndustriesPage"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminLeads = lazy(() => import("@/pages/admin/AdminLeads"));
const AdminBlog = lazy(() => import("@/pages/admin/AdminBlog"));
const AdminSEO = lazy(() => import("@/pages/admin/AdminSEO"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminPages = lazy(() => import("@/pages/admin/AdminPages"));
const AdminPageEditor = lazy(() => import("@/pages/admin/AdminPageEditor"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminMFA = lazy(() => import("@/pages/admin/AdminMFA"));
const ROICalculatorPage = lazy(() => import("@/pages/ROICalculatorPage"));
const CaseStudiesPage = lazy(() => import("@/pages/CaseStudiesPage"));
const CaseStudyDetailPage = lazy(() => import("@/pages/CaseStudyDetailPage"));
const CybersecurityAssessmentPage = lazy(() => import("@/pages/CybersecurityAssessmentPage"));
const VancouverLawFirmsPage = lazy(() => import("@/pages/VancouverLawFirmsPage"));
const AdminCaseStudies = lazy(() => import("@/pages/admin/AdminCaseStudies"));
const ChatWidget = lazy(() => import("@/components/ChatWidget"));
const Toaster = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

const Loader = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Clean up any injected elements outside #root
const cleanBody = () => {
  document.querySelectorAll('body > *').forEach(el => {
    if (el.id === 'root' || el.tagName === 'SCRIPT' || el.tagName === 'NOSCRIPT') return;
    el.remove();
  });
};

const bodyObserver = new MutationObserver(() => cleanBody());

// Scroll to top on route change, handle hash navigation.
// Uses `useLayoutEffect` (before paint) + manual scrollRestoration so the
// browser does not re-apply a stale scroll position on routes that share a
// `<Route>` definition (e.g. PageResolver's `/:slug` catch-all serving both
// location pages and service×city pages). A small setTimeout also re-asserts
// `scrollTo(0,0)` once the lazy-loaded child finishes mounting, since the
// initial scroll runs while Suspense is still showing the fallback.
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  React.useLayoutEffect(() => {
    // Disable browser's automatic scroll restoration on history pushes.
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return;
    }
    // Hard reset across html/body/window for cross-browser coverage.
    const resetScroll = () => {
      window.scrollTo(0, 0);
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    };
    resetScroll();
    // Re-assert after the next paint so any post-mount layout shifts
    // (lazy chunks, CMS content fetch, image load) don't restore a stale
    // scroll position.
    const t1 = setTimeout(resetScroll, 50);
    const t2 = setTimeout(resetScroll, 250);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [pathname, hash]);
  return null;
};

function App() {
  const [chatReady, setChatReady] = React.useState(false);

  useEffect(() => {
    cleanBody();
    bodyObserver.observe(document.body, { childList: true });
    return () => bodyObserver.disconnect();
  }, []);

  // Defer chat widget until the browser is idle (or after first interaction)
  // — keeps initial JS bundle lean and improves mobile LCP/TBT.
  useEffect(() => {
    if (chatReady) return;
    const arm = () => setChatReady(true);
    let idleId;
    const ric = window.requestIdleCallback;
    if (typeof ric === 'function') {
      idleId = ric(arm, { timeout: 4000 });
    } else {
      idleId = setTimeout(arm, 3500);
    }
    const earlyTriggers = ['scroll', 'pointerdown', 'keydown', 'touchstart'];
    const onEarly = () => arm();
    earlyTriggers.forEach(ev => window.addEventListener(ev, onEarly, { once: true, passive: true }));
    return () => {
      if (typeof window.cancelIdleCallback === 'function' && idleId) window.cancelIdleCallback(idleId);
      else clearTimeout(idleId);
      earlyTriggers.forEach(ev => window.removeEventListener(ev, onEarly));
    };
  }, [chatReady]);

  return (
    <div className="App min-h-screen bg-[#050505]">
      <SiteSettingsProvider>
        <AuthProvider>
          <InlineEditProvider>
          <BrowserRouter>
            <ScrollToTop />
            <UniversalTextEditor />
            <Suspense fallback={<Loader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/post/:slug" element={<BlogPostPage />} />
                <Route path="/industries" element={<IndustriesPage />} />
                <Route path="/lp/:service" element={<LandingPage />} />
                <Route path="/roi-calculator" element={<ROICalculatorPage />} />
                <Route path="/cybersecurity-assessment" element={<CybersecurityAssessmentPage />} />
                <Route path="/it-support-for-vancouver-law-firms" element={<VancouverLawFirmsPage />} />
                <Route path="/case-studies" element={<CaseStudiesPage />} />
                <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />
                <Route path="/:slug" element={<PageResolver />} />
                
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/pages" element={<ProtectedRoute><AdminPages /></ProtectedRoute>} />
                <Route path="/admin/pages/:pageType/*" element={<ProtectedRoute><AdminPageEditor /></ProtectedRoute>} />
                <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
                <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
                <Route path="/admin/seo" element={<ProtectedRoute><AdminSEO /></ProtectedRoute>} />
                <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
                <Route path="/admin/case-studies" element={<ProtectedRoute><AdminCaseStudies /></ProtectedRoute>} />
                <Route path="/admin/security" element={<ProtectedRoute><AdminMFA /></ProtectedRoute>} />
              </Routes>
            </Suspense>
            <Suspense fallback={null}>
              {chatReady && <ChatWidget />}
            </Suspense>
            <CookieBanner />
            <InlineEditToggle />
          </BrowserRouter>
          </InlineEditProvider>
        </AuthProvider>
      </SiteSettingsProvider>
      <Suspense fallback={null}>
        <Toaster position="top-right" theme="dark" />
      </Suspense>
    </div>
  );
}

export default App;
